import {
  useState,
  useRef,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";
import { CurrenciesBottomSheet } from "./bottom-sheets/CurrenciesBottomSheet";
import { CURRENCIES } from "@/constants/currencies";

const classNames = (...classes: (string | undefined | boolean)[]) => {
  return classes.filter(Boolean).join(" ");
};

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  /** Initial currency to display */
  defaultCurrency?: string;
  /** Initial price value to display */
  defaultValue?: string | number;
  /** Label text displayed above the input */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Element to display before the input */
  prefix?: ReactNode;
  /** Element to display after the input */
  suffix?: ReactNode;
  /** Function called when prefix is clicked */
  onPrefixClick?: (e: React.MouseEvent) => void;
  /** Function called when suffix is clicked */
  onSuffixClick?: (e: React.MouseEvent) => void;
  /** Additional class names for the input container */
  containerClassName?: string;
  /** Additional class names for the input element */
  inputClassName?: string;
  /** Additional class names for the label */
  labelClassName?: string;
  /** Additional class names for the error message */
  errorClassName?: string;
  /** Additional onChange for currency */
  onChangeCurrency?: (currency: string) => void;
  /** Additional onChange for price point input */
  onChangeInput?: React.ChangeEventHandler<HTMLInputElement> | undefined;
}

const CustomInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error,
      onChangeCurrency,
      onChangeInput,
      defaultCurrency,
      defaultValue,
      label,
      labelClassName,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currency, setCurrency] = useState<string>(
      defaultCurrency ?? "USD"
    );
    const dropdownRef = useRef<HTMLDivElement>(null);
    return (
      <>
        <div className={classNames("w-full", containerClassName)}>
          {label && (
            <label
              htmlFor={props.id}
              className={classNames(
                "block text-sm text-[#858585] font-normal mb-2",
                labelClassName
              )}
            >
              {label}
            </label>
          )}
          <div className="relative flex" ref={dropdownRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={classNames(
                  "px-3 py-4 max-w-[100px] bg-white border rounded-l-xl text-sm flex items-center justify-between gap-2 focus:outline-none focus:ring-0 hover:bg-gray-50",
                  error ? "border-red-500" : "border-[#e0e0e0]"
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="text-black">
                    {CURRENCIES.find((c) => c.code === currency)?.symbol}
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-black transition-transform ${isOpen ? "rotate-180" : ""
                    }`}
                />
              </button>
            </div>

            {/* Input Field */}
            <div className="relative flex-1">
              <input
                ref={ref}
                type="number"
                id="price"
                placeholder="0.00"
                defaultValue={defaultValue}
                onChange={onChangeInput}
                className={classNames(
                  "block w-full pl-3 pr-3 border border-l-0 rounded-r-xl focus:outline-none focus:ring-0 text-base py-3.5 text-black",
                  error ? "border-red-500" : "border-[#e0e0e0]"
                )}
              />
            </div>
          </div>
          {error && (
            <p className={classNames("mt-1 text-xs text-red-500")}>{error}</p>
          )}
        </div>
        <CurrenciesBottomSheet
          open={isOpen}
          setOpen={setIsOpen}
          currency={currency}
          setCurrency={(curr) => {
            onChangeCurrency?.(curr);
            setCurrency(curr);
          }}
        />
      </>
    );
  }
);

export default CustomInput;
